<template>
  <div>
    <!-- Disbursements Dashboard Section -->
    <div class="dashboard-section">

      <!-- Dashboard Cards -->
      <div class="cards-container">
        <div
          class="card-wrapper"
          v-for="(card, index) in dashboardCards"
          :key="index"
        >
          <div class="card-content">
            <div class="card-header">
              <div>
                <h3>{{ card.title }}</h3>
                <p v-if="!statsLoading">{{ card.description }}</p>
                <p v-else>-</p>
              </div>
              <div
                class="icon-wrapper"
                :style="{ backgroundColor: card.iconBg, color: card.iconColor }"
              >
                <a-icon :class="['fa', card.icon]" />
              </div>
            </div>
            <p class="card-paragraph">
              <span
                v-if="card.spanIcon || card.span"
                :style="{
                  color:
                    card.spanIcon === 'fa-arrow-down' ? '#ef4444' : '#10b981',
                  display: 'inline-flex',
                  alignItems: 'center',
                }"
              >
                <i
                  :class="['fa', card.spanIcon]"
                  style="font-size: 14px; margin-right: 4px"
                ></i>
                {{ card.span }}
              </span>
              {{ card.content }}
            </p>
          </div>
        </div>
      </div>
    </div>

    <!-- Search and Filter Section -->
    <div style="margin-top: 40px">
      <a-row :gutter="16">
        <a-col :span="6">
          <p style="margin: 0px">Search</p>
          <a-input
            v-model="search"
            placeholder="Search by disbursement ID, factoring request ID, or seller name"
            style="width: 100%"
            allow-clear
            prefix-icon="search"
          />
        </a-col>
        <a-col :span="6">
          <p style="margin: 0px">Status</p>
          <a-select
            v-model="filters.status"
            placeholder="Filter by Status"
            allowClear
            style="width: 100%"
          >
            <a-select-option value="success">Success</a-select-option>
            <a-select-option value="pending">Pending</a-select-option>
            <a-select-option value="failed">Failed</a-select-option>
          </a-select>
        </a-col>
        <a-col :span="6">
          <p style="margin: 0px">Date Range</p>
          <a-range-picker
            v-model="filters.dateRange"
            placeholder="['Start Date', 'End Date']"
            style="width: 100%"
            format="YYYY-MM-DD"
            :allowClear="true"
          />
        </a-col>
      </a-row>
    </div>

    <!-- Disbursements Table -->
    <div class="table-section">
      <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 16px;">
        <h3 style="margin: 0;">Disbursements Overview</h3>
        <a-button
          type="primary"
          icon="plus"
          style="background: #26a246; border-color: #26a246; border-radius: 6px;"
          @click="openNewDisbursementDrawer"
        >
          New Disbursement Request
        </a-button>
      </div>
      <a-table
        :columns="disbursementColumns"
        :data-source="disbursements"
        row-key="disbursementId"
        :pagination="tablePagination"
        :scroll="{ x: 'max-content' }"
        :loading="tableLoading"
        style="background-color: #fafafa !important"
        :rowClassName="() => 'custom-table-row'"
        @change="handleTableChange"
      >
        <template slot="status" slot-scope="text">
          <StatusTag :status="text" />
        </template>
        <template slot="amount" slot-scope="text">
          {{ formatAmount(text) }}
        </template>
        <template slot="actions" slot-scope="text, record">
          <a-button type="link" @click="viewDisbursement(record)">
            <a-icon type="eye" style="color: #9e9e9e" />
          </a-button>
        </template>
      </a-table>
    </div>

    <!-- New Disbursement Request Component -->
    <NewDisbursementRequest
      :visible="showNewDisbursementDrawer"
      @close="closeNewDisbursementDrawer"
      @success="onDisbursementSuccess"
    />

    <!-- Disbursement Details Drawer -->
    <DisbursementDetailsDrawer
      :visible="showDetailsDrawer"
      :disbursementData="selectedDisbursement"
      @close="closeDetailsDrawer"
    />
  </div>
</template>

<script>
import NewDisbursementRequest from "./NewDisbursementRequest.vue";
import DisbursementDetailsDrawer from "./DisbursementDetailsDrawer.vue";
import FinancingAndPaymentsService from "@/api/financing-and-payments.service.js";
import StatusTag from "../shared-components/StatusTag.vue";
import { formatAmount, formatDate } from "@/utils";

export default {
  name: "DisbursementsTab",
  components: {
    NewDisbursementRequest,
    DisbursementDetailsDrawer,
    StatusTag,
  },
  data() {
    return {
      // Search and filters
      search: "",
      searchDebounceTimer: null,
      filters: {
        status: undefined,
        dateRange: null,
      },

      // Pagination
      pagination: {
        pageIndex: 1,
        pageSize: 10,
        totalCount: 0,
      },

      // Table data and loading
      disbursements: [],
      tableLoading: false,

      // UI state
      showNewDisbursementDrawer: false,
      showDetailsDrawer: false,
      selectedDisbursement: null,

      // Statistics loading state
      statsLoading: false,

      dashboardCards: [
        {
          title: "Total Disbursements",
          description: "0",
          icon: "fa-credit-card",
          iconColor: "#05004E",
          iconBg: "#e0e7ff",
          span: "",
          spanIcon: "",
          content: "",
        },
        {
          title: "Successful",
          description: "0",
          icon: "fa-check-circle",
          iconColor: "#10b981",
          iconBg: "#d1fae5",
          span: "",
          spanIcon: "",
          content: "",
        },
        {
          title: "Pending",
          description: "0",
          icon: "fa-clock",
          iconColor: "#f59e0b",
          iconBg: "#fef3c7",
          span: "",
          spanIcon: "",
          content: "",
        },
        {
          title: "Failed",
          description: "0",
          icon: "fa-times-circle",
          iconColor: "#ef4444",
          iconBg: "#fee2e2",
          span: "",
          spanIcon: "",
          content: "",
        },
      ],
      disbursementColumns: [
        {
          title: "Disbursement ID",
          dataIndex: "disbursementId",
          key: "disbursementId",
          width: 150,
        },
        {
          title: "Factoring Request ID",
          dataIndex: "factoringRequestId",
          key: "factoringRequestId",
          width: 150,
        },
        {
          title: "Seller Name",
          dataIndex: "sellerName",
          key: "sellerName",
          width: 200,
        },
        {
          title: "Amount",
          dataIndex: "amount",
          key: "amount",
          scopedSlots: { customRender: "amount" },
          width: 150,
        },
        {
          title: "Status",
          dataIndex: "status",
          key: "status",
          scopedSlots: { customRender: "status" },
          width: 120,
        },
        {
          title: "Created On",
          dataIndex: "createdOn",
          key: "createdOn",
          width: 120,
        },
        {
          title: "Sent on",
          dataIndex: "sentOn",
          key: "sentOn",
          width: 120,
        },
        {
          title: "Action",
          key: "actions",
          scopedSlots: { customRender: "actions" },
          width: 80,
        },
      ],
    };
  },
  computed: {
    tablePagination() {
      return {
        current: this.pagination.pageIndex,
        pageSize: this.pagination.pageSize,
        total: this.pagination.totalCount,
        showTotal: (total, range) =>
          `${range[0]}-${range[1]} of ${total} items`,
        showSizeChanger: false,
      };
    },
  },
  mounted() {
    this.fetchData();
    this.fetchStats();
  },
  watch: {
    "pagination.pageIndex"() {
      this.fetchData();
    },
    search() {
      if (this.searchDebounceTimer) clearTimeout(this.searchDebounceTimer);
      this.searchDebounceTimer = setTimeout(() => {
        this.pagination.pageIndex = 1;
        this.fetchData();
      }, 400);
    },
    filters: {
      handler() {
        this.pagination.pageIndex = 1;
        this.fetchData();
      },
      deep: true,
    },
  },
  methods: {
    formatAmount,
    formatDate,
    async fetchData() {
      this.tableLoading = true;

      try {
        // Convert dateRange to startDate and endDate
        let startDate = "";
        let endDate = "";

        if (this.filters.dateRange && this.filters.dateRange.length === 2) {
          // Create Date objects with time 00:00:00 for start date
          const startDateObj = new Date(
            this.filters.dateRange[0].format("YYYY-MM-DD")
          );
          startDateObj.setHours(0, 0, 0, 0);
          startDate = startDateObj;

          // Create Date objects with time 23:59:59 for end date
          const endDateObj = new Date(
            this.filters.dateRange[1].format("YYYY-MM-DD")
          );
          endDateObj.setHours(23, 59, 59, 999);
          endDate = endDateObj;
        }

        const params = {
          pageIndex: this.pagination.pageIndex,
          pageSize: this.pagination.pageSize,
          status: this.filters.status || "",
          search: this.search || "",
          startDate: startDate,
          endDate: endDate,
        };

        const response = await FinancingAndPaymentsService.getDisbursements(
          params
        );

        if (response.data?.code === 200) {
          const data = response.data.data;
          this.disbursements = (data.results || []).map((item) => ({
            disbursementId: item.id || "N/A",
            factoringRequestId: item.factoringId || "N/A",
            sellerName: item.invoice.sellerName || "N/A",
            amount: item.amount || 0,
            status: item.status || "Unknown",
            terraResponseMessage: item.terraResponseMessage || null,
            createdOn: item.createdAt ? formatDate(item.createdAt) : "N/A",
            sentOn: item.completedAt ? formatDate(item.completedAt) : "N/A",
          }));
          this.pagination.totalCount = data.totalCount || 0;
        } else {
          this.disbursements = [];
          this.pagination.totalCount = 0;
          if (response.data?.message) {
            this.$message.warning(response.data.message);
          }
        }
      } catch (error) {
        console.error("Error fetching disbursements:", error);
        this.disbursements = [];
        this.pagination.totalCount = 0;
        this.$message.error("Failed to fetch disbursements. Please try again.");
      } finally {
        this.tableLoading = false;
      }
    },

    async fetchStats() {
      this.statsLoading = true;
      try {
        const response =
          await FinancingAndPaymentsService.getDisbursementStatistics();

        if (response.status === 200 && response.data?.data) {
          const statsData = response.data.data.data;
          // Map the API response to existing dashboard cards
          statsData.forEach((stat) => {
            switch (stat.status) {
              case "TotalDisbursement":
                this.dashboardCards[0].description = stat.count.toString();
                break;
              case "Success":
                this.dashboardCards[1].description = stat.count.toString();
                break;
              case "Pending":
                this.dashboardCards[2].description = stat.count.toString();
                break;
              case "Failed":
                this.dashboardCards[3].description = stat.count.toString();
                break;
            }
          });
        }
      } catch (error) {
        console.error("Error fetching stats:", error);
      } finally {
        this.statsLoading = false;
      }
    },

    handleTableChange(pagination) {
      if (pagination.current !== this.pagination.pageIndex) {
        this.pagination.pageIndex = pagination.current;
      }
    },

    clearFilters() {
      this.filters = {
        status: undefined,
        dateRange: null,
      };
      this.search = "";
    },

    viewDisbursement(record) {
      this.selectedDisbursement = record;
      this.showDetailsDrawer = true;
    },

    openNewDisbursementDrawer() {
      this.showNewDisbursementDrawer = true;
    },

    closeNewDisbursementDrawer() {
      this.showNewDisbursementDrawer = false;
    },

    closeDetailsDrawer() {
      this.showDetailsDrawer = false;
      this.selectedDisbursement = null;
    },

    async onDisbursementSuccess() {
      await this.fetchData();
      await this.fetchStats();
    },
  },
};
</script>

<style scoped>
.dashboard-section {
  margin-bottom: 32px;
}

.dashboard-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
}

.dashboard-header h2 {
  margin: 0;
  font-size: 20px;
  font-weight: 600;
}

.time-period-selector {
  display: flex;
  align-items: center;
}

.cards-container {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  justify-content: flex-start;
  width: 100%;
}

.card-wrapper {
  flex: 0 0 calc(25% - 12px);
  max-width: calc(25% - 12px);
  min-width: 200px;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1);
  transition: all 0.3s ease;
}

.card-content {
  padding: 20px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  min-height: 120px;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 16px;
  background-color: transparent;
  padding: 0;
  border-bottom: none;
}

.card-header p {
  font-weight: 700;
  font-size: 18px;
  color: #000000;
  margin: 0;
}

.card-header h3 {
  font-size: 0.9em;
  color: #6b7280;
  margin: 0 0 4px 0;
}

.card-paragraph {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  margin: 0;
  line-height: 1.4;
  margin-top: auto;
  color: #6b7280;
}

.icon-wrapper {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  font-size: 12px;
  flex-shrink: 0;
}

.table-section {
  background: #fff;
  padding: 24px;
  border-radius: 8px;
}

.table-section h3 {
  margin: 0 0 16px 0;
  font-size: 18px;
  font-weight: 600;
}

/* Responsive */
@media (max-width: 1400px) {
  .card-wrapper {
    flex: 0 0 calc(33.333% - 10px);
    max-width: calc(33.333% - 10px);
  }
}

@media (max-width: 1200px) {
  .card-wrapper {
    flex: 0 0 calc(50% - 8px);
    max-width: calc(50% - 8px);
  }
}

@media (max-width: 768px) {
  .card-wrapper {
    flex: 0 0 calc(50% - 8px);
    max-width: calc(50% - 8px);
  }
}

@media (max-width: 576px) {
  .card-wrapper {
    flex: 0 0 100%;
    max-width: 100%;
  }

  .dashboard-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
  }
}
</style>
